import { useEffect } from "react";
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";
import { useQueryClient } from "@tanstack/react-query";
import { gql } from "@/services/httpAdapter";
import { IReconciliationStatus } from "@/DataPages/types/inventory/models/reconciliation";

// Helpers
import { or } from "@/Common/helpers/sonar";

type TUseReconciliationStatus = {
	locationId?: string;
	commissionCycleId?: string;
	payPeriodId?: string;
};

const useReconciliationStatusSubscription = (params: TUseReconciliationStatus) => {
	const { locationId = "", commissionCycleId = "", payPeriodId = "" } = params;
	const queryClient = useQueryClient();
	const { addSubscription, getSubscription, deleteSubscription } = useSubscriptions();

	const setReconciliationStatusSubscriptions = getSubscription(["onSetReconciliationStatus", locationId, commissionCycleId, payPeriodId]);

	setReconciliationStatusSubscriptions?.subscriptions?.onSetReconciliationStatus?.setCallback(
		(result: { data: { onSetReconciliationStatus: IReconciliationStatus } }) => {
			const { onSetReconciliationStatus: newData } = result.data;

			queryClient.setQueriesData<IReconciliationStatus>(
				{ queryKey: ["inventory", "getReconciliationStatus", locationId, commissionCycleId, payPeriodId] },
				(cachedData?: IReconciliationStatus) => {
					if (isThereAnyNewFinalizedType(newData, cachedData)) {
						queryClient.invalidateQueries({
							queryKey: ["inventory", "reconciliation", locationId, commissionCycleId, payPeriodId],
						});
					}

					return newData;
				}
			);
		}
	);

	function isThereAnyNewFinalizedType(newData: IReconciliationStatus, oldData?: Partial<IReconciliationStatus>) {
		const newFinalized = isStatusFinalized(oldData?.newVehicleReconciliation?.status, newData?.newVehicleReconciliation?.status);
		const usedFinalized = isStatusFinalized(oldData?.usedVehicleReconciliation?.status, newData?.usedVehicleReconciliation?.status);
		return or(newFinalized, usedFinalized);
	}

	function isStatusFinalized(oldStatus = "", newStatus = "") {
		return oldStatus === "RUNNING" && newStatus === "FINALIZED";
	}

	useEffect(() => {
		if (locationId) {
			addSubscription(
				["onSetReconciliationStatus", locationId, commissionCycleId, payPeriodId],
				[
					{
						context: "onSetReconciliationStatus",
						query: gql`
							subscription onSetReconciliationStatus($locationId: String, $commissionCycleId: String, $payPeriodId: String) {
								onSetReconciliationStatus(
									locationId: $locationId
									commissionCycleId: $commissionCycleId
									payPeriodId: $payPeriodId
								) {
									_id
									commissionCycleId
									locationId
									payPeriodId
									newVehicleReconciliation {
										status
										startDate
										endDate
										error
									}
									usedVehicleReconciliation {
										status
										startDate
										endDate
										error
									}
								}
							}
						`,
						variables: { locationId, commissionCycleId, payPeriodId },
					},
				]
			);
		}
		return () => {
			deleteSubscription(["onSetReconciliationStatus", locationId, commissionCycleId, payPeriodId]);
		};
	}, [locationId, commissionCycleId, payPeriodId]);
};

export default useReconciliationStatusSubscription;
